import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FleetTabs, PageHeader, Pill, fmtDate, errText } from './common';

const code = { display: 'block', whiteSpace: 'pre-wrap', fontSize: 12, padding: 10, borderRadius: 6, background: 'var(--bg-secondary, rgba(127,127,127,.08))', margin: '6px 0 12px' };

export default function FleetEnrollment() {
  const [tokens, setTokens] = useState([]);
  const [form, setForm] = useState({ label: '', expires_days: 14, max_uses: '', tags: '' });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get('/cve-fleet/tokens'); setTokens(r.data.tokens); }
    catch (e) { setError(errText(e, 'Failed to load tokens')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await axios.post('/cve-fleet/tokens', {
        label: form.label, expires_days: Number(form.expires_days) || 14,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      });
      setCreated(r.data); setForm({ label: '', expires_days: 14, max_uses: '', tags: '' }); load();
    } catch (err) { setError(errText(err, 'Failed to create the token')); }
    finally { setBusy(false); }
  }
  async function revoke(t) {
    if (!window.confirm(`Revoke "${t.label}"? Servers that already enrolled keep working; new enrollments with it are refused.`)) return;
    try { await axios.post(`/cve-fleet/tokens/${t.id}/revoke`); load(); } catch (e) { alert(errText(e, 'Failed to revoke')); }
  }

  const installer = `${window.location.origin}/api/cve-fleet/install.sh`;
  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        <div className="card" style={{ marginBottom: 16, fontSize: 13 }}>
          <h4 style={{ marginTop: 0 }}>Install the agent on a server</h4>
          <p style={{ margin: '4px 0' }}>Download the installer (one file; it contains no secrets) and run it as root. Run it from a saved file, not through a pipe.</p>
          <code style={code}>{`curl -fsSLo cve-agent-install.sh ${installer}`}</code>
          <p style={{ margin: '4px 0' }}>Check a server without installing anything:</p>
          <code style={code}>sudo bash cve-agent-install.sh --detect-only</code>
          <p style={{ margin: '4px 0' }}>Install, with a token from below. The environment variable keeps the token out of shell history:</p>
          <code style={code}>{"sudo CVE_AGENT_TOKEN='<token>' bash cve-agent-install.sh --tags prod,sip-edge"}</code>
          <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>The server needs systemd, Python 3.6+, openssl, nftables, and outbound HTTPS to this site on port 443. The agent opens no inbound port and runs only the seven listed actions.</p>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h4 style={{ marginTop: 0 }}>New enrollment token</h4>
          <form onSubmit={create}>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Label (who or what it is for)</label>
                <input className="form-input" required value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="SIP edge rollout, Nov 2026" /></div>
              <div className="form-group"><label className="form-label">Expires after (days)</label>
                <input className="form-input" type="number" min={1} max={365} value={form.expires_days} onChange={e => setForm({ ...form, expires_days: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Max servers (blank = no limit)</label>
                <input className="form-input" type="number" min={1} value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Tags added to every server</label>
                <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="prod,sip-edge" /></div>
            </div>
            {error && <div className="error-msg">{error}</div>}
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create token'}</button>
          </form>
          {created && (
            <div className="card" style={{ marginTop: 12, borderColor: 'var(--warning)' }}>
              <strong>Copy this token now; it is not shown again.</strong>
              <code style={code}>{created.token}</code>
              <button className="btn btn-secondary" onClick={() => { navigator.clipboard?.writeText(created.token); }}>Copy</button>{' '}
              <button className="btn btn-ghost" onClick={() => setCreated(null)}>Done</button>
            </div>
          )}
        </div>

        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
            <th style={{ padding: 8 }}>Label</th><th style={{ padding: 8 }}>Token</th><th style={{ padding: 8 }}>Used</th>
            <th style={{ padding: 8 }}>Tags</th><th style={{ padding: 8 }}>Expires</th><th style={{ padding: 8 }}>State</th>
            <th style={{ padding: 8 }}>Created</th><th style={{ padding: 8 }}></th></tr></thead>
          <tbody>{tokens.map(t => (
            <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: 8 }}>{t.label}</td>
              <td style={{ padding: 8 }}><code>{t.token_prefix}…</code></td>
              <td style={{ padding: 8 }}>{t.uses}{t.max_uses ? ` / ${t.max_uses}` : ''}</td>
              <td style={{ padding: 8 }}>{t.default_tags.join(', ')}</td>
              <td style={{ padding: 8 }}>{fmtDate(t.expires_at)}</td>
              <td style={{ padding: 8 }}>{t.revoked_at ? <Pill label="revoked" /> : t.active ? <Pill label="active" color="var(--success)" /> : <Pill label="expired or used up" />}</td>
              <td style={{ padding: 8, fontSize: 12 }}>{t.created_by} · {fmtDate(t.created_at)}</td>
              <td style={{ padding: 8 }}>{!t.revoked_at && <button className="btn btn-ghost" style={{ color: 'var(--danger)', fontSize: 12 }} onClick={() => revoke(t)}>Revoke</button>}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
